import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Wallet, Package, TrendingUp, Landmark, ShoppingCart, Banknote, Receipt } from "lucide-react";import { api } from "../lib/api";
import { rupiah, tgl } from "../lib/format";

const STAT_INITIAL = { opacity: 0, y: 8 };
const STAT_ANIMATE = { opacity: 1, y: 0 };
const statTransition = (i) => ({ delay: i * 0.05, duration: 0.25 });

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="py-20 text-center text-sm text-muted-foreground" data-testid="dashboard-loading">Memuat dashboard...</div>;

  const stats = [
    { label: "Kas", value: data.kas, icon: Wallet, color: "text-primary", testid: "stat-kas" },
    { label: "Bank", value: data.bank, icon: Landmark, color: "text-sky-600", testid: "stat-bank" },
    { label: "Nilai Stok", value: data.nilai_stok, icon: Package, color: "text-amber-600", testid: "stat-stok" },
    { label: "Hutang", value: data.hutang, icon: Receipt, color: "text-red-600", testid: "stat-hutang" },
    { label: "Penjualan Bulan Ini", value: data.penjualan_bulan_ini, icon: TrendingUp, color: "text-emerald-600", testid: "stat-penjualan-bulan" },
    { label: "Laba Bulan Ini", value: data.laba_bulan_ini, icon: Banknote, color: data.laba_bulan_ini >= 0 ? "text-emerald-600" : "text-red-600", testid: "stat-laba-bulan" },
  ];

  const actions = [
    { to: "/pembelian", label: "Beli Barang", icon: ShoppingCart, testid: "action-beli" },
    { to: "/penjualan", label: "Jual Barang", icon: Banknote, testid: "action-jual" },
    { to: "/jurnal", label: "Jurnal Biaya", icon: Receipt, testid: "action-jurnal" },
    { to: "/hutang", label: "Catat Hutang", icon: Landmark, testid: "action-hutang" },
  ];

  return (
    <div data-testid="dashboard-page">
      <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground">Ringkasan Usaha</p>
      <h1 className="mt-1 text-2xl font-bold tracking-tight" style={{ fontFamily: "'Work Sans', sans-serif" }}>Dashboard</h1>

      <div className="mt-5 grid grid-cols-2 md:grid-cols-3 gap-3">
        {stats.map((s, i) => (
          <motion.div key={s.label} initial={STAT_INITIAL} animate={STAT_ANIMATE} transition={statTransition(i)}
            className="rounded-md border border-border bg-white p-4" data-testid={s.testid}>
            <div className="flex items-center gap-2">
              <s.icon className={`h-4 w-4 ${s.color}`} />
              <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-muted-foreground">{s.label}</p>
            </div>
            <p className="mt-2 text-lg font-bold tracking-tight" style={{ fontFamily: "'Work Sans', sans-serif" }}>{rupiah(s.value)}</p>
          </motion.div>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
        {actions.map((a) => (
          <Link key={a.to} to={a.to} data-testid={a.testid}
            className="flex items-center gap-2 rounded-md border border-border bg-white p-3.5 text-sm font-semibold hover:-translate-y-[2px] hover:shadow-sm transition-[transform,box-shadow] duration-150 active:scale-[0.98]">
            <a.icon className="h-4 w-4 text-primary" /> {a.label}
          </Link>
        ))}
      </div>

      <div className="mt-8">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-bold tracking-tight" style={{ fontFamily: "'Work Sans', sans-serif" }}>Penjualan Terakhir</h2>
          <span className="text-xs text-muted-foreground" data-testid="stat-unit-tersedia">{data.unit_tersedia} unit tersedia</span>
        </div>
        <div className="mt-3 rounded-md border border-border bg-white divide-y divide-border">
          {data.penjualan_terakhir.length === 0 && <p className="p-4 text-sm text-muted-foreground">Belum ada penjualan.</p>}
          {data.penjualan_terakhir.map((s) => (
            <div key={s.id} className="flex items-center justify-between p-3.5" data-testid={`recent-sale-${s.id}`}>
              <div>
                <p className="text-sm font-semibold">{s.nama}</p>
                <p className="text-xs text-muted-foreground">{s.kode} · {tgl(s.tanggal)} · {s.metode === "transfer" ? "Transfer Bank" : "Tunai"}</p>
              </div>
              <p className="text-sm font-bold text-emerald-600">{rupiah(s.harga_jual)}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
